import React from 'react'
// c:\xampp\htdocs\miners-market-main\pages\PlatformFeesPage.js

export default function PlatformFeesPage() {
    const [flatCategories, setFlatCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState(null);
    const [feeChanges, setFeeChanges] = React.useState({});

     // Get the global default fee from the window config, or use a fallback.
    const globalDefaultFee = (window.APP_CONFIG?.platformFeePercentage || 0.06) * 100;

    const fetchFees = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/api/platform_fees.php', { credentials: 'include' });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to fetch platform fees.');
            }
            const data = await response.json();
            setFlatCategories(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchFees();
    }, [fetchFees]);

    // Create a hierarchical structure from the flat list of categories.
    const categoryTree = React.useMemo(() => {
        if (!flatCategories || flatCategories.length === 0) {
            return [];
        }

        // 1. Prepare nodes and normalize IDs
        let nodes = flatCategories.map(cat => ({
            ...cat,
            id: parseInt(cat.id, 10),
            parent_id: cat.parent_id ? parseInt(cat.parent_id, 10) : null,
            children: [] // Initialize children
        }));

        // 2. Helper to find or create a virtual category (grouper)
        // We give virtual nodes negative IDs to avoid colliding with real DB IDs
        let virtualIdCounter = -1;
        const ensureCategory = (name, slug) => {
            let cat = nodes.find(n => n.name.toLowerCase() === name.toLowerCase() || n.slug === slug);
            if (!cat) {
                cat = {
                    id: virtualIdCounter--,
                    name: name,
                    slug: slug || name.toLowerCase().replace(/\s+/g, '-'),
                    parent_id: null,
                    fee_percentage: null,
                    children: [],
                    isVirtual: true // Mark as virtual so we don't try to save fees for it
                };
                nodes.push(cat);
            }
            return cat;
        };

        // 3. Define the Desired Main Categories
        const equipmentNode = ensureCategory('Equipment', 'equipment');
        const commercialNode = ensureCategory('Commercial', 'commercial');
        const componentsNode = ensureCategory('Components', 'components');
        const rentalsNode = ensureCategory('Rentals', 'rentals');
        const rebuildsNode = ensureCategory('Rebuilds', 'rebuilds');
        // Consumables and Miscellaneous usually exist, but ensure them just in case
        const miscNode = ensureCategory('Miscellaneous', 'miscellaneous'); 
        // Note: Your DB might have "Miscellaneous Items", verify normalization:
        const existingMisc = nodes.find(n => n.name === 'Miscellaneous Items');
        const targetMisc = existingMisc || miscNode;

        // 4. Force Re-parenting logic
        const normalize = str => str ? str.toLowerCase().trim() : '';
        
        nodes.forEach(node => {
            // Only move nodes that are currently at the root (parent_id is null)
            // and are NOT the main categories themselves.
            if (!node.parent_id && !node.isVirtual && node.id !== targetMisc.id) {
                const name = normalize(node.name);
                
                // Map to Miscellaneous
                if (['bearings', 'filters', 'conveyor belts', 'greases', 'diesel', 'kg pps'].includes(name)) {
                    node.parent_id = targetMisc.id;
                }
                // Map to Equipment
                else if (['construction equipment', 'plant', 'surface equipment', 'transportation', 'underground equipment'].includes(name)) {
                    node.parent_id = equipmentNode.id;
                }
                // Map to Commercial
                else if (['mines', 'ore sales', 'refurbishing services'].includes(name)) {
                    node.parent_id = commercialNode.id;
                }
                // Map to Rentals
                else if (['rental equipment'].includes(name)) {
                    node.parent_id = rentalsNode.id;
                }
            }
        });

        // 5. Build Tree
        const categoryMap = new Map(nodes.map(n => [n.id, n]));
        const tree = [];

        nodes.forEach(node => {
            if (node.parent_id && categoryMap.has(node.parent_id)) {
                categoryMap.get(node.parent_id).children.push(node);
            } else {
                // Only push top-level nodes (our Main Categories)
                // Filter out empty virtual nodes if you prefer, but here we keep them 
                // if they have children or are main structure.
                tree.push(node);
            }
        });

        // 6. recursive Sort
        const sortTree = (nodes) => {
            const sortByName = (a, b) => a.name.localeCompare(b.name);
            nodes.sort(sortByName);
            nodes.forEach(node => node.children && sortTree(node.children));
        };
        sortTree(tree);

        return tree;
    }, [flatCategories]);

    const handleFeeChange = (categoryId, value) => {
        // If the value is an empty string, we want to set it to null to signify deletion/reset.
        if (value === '') {
            setFeeChanges(prev => ({ ...prev, [categoryId]: null }));
            return;
        }
        const numValue = parseFloat(value);
        // Only update if it's a valid number to prevent NaN in state.
        if (!isNaN(numValue)) {
            setFeeChanges(prev => ({ ...prev, [categoryId]: numValue }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        // Prepare payload: send all changed values, including nulls for deletion.
        const payload = Object.entries(feeChanges)
            .map(([categoryId, fee]) => ({
                category_id: parseInt(categoryId, 10),
                fee_percentage: fee === null ? null : parseFloat(fee)
            }));

        if (payload.length === 0) {
            setError("No changes to save.");
            setIsSaving(false);
            return;
        }

        try {
            const response = await fetch('/api/platform_fees.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Failed to save fees.');
            }
            setSuccessMessage(result.message);
            setFeeChanges({}); // Clear changes after successful save
            // Refetch data to confirm and show updated inherited fees
            fetchFees();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const renderCategoryRows = (categories, level = 0, inheritedFee = globalDefaultFee) => {
        return categories.map(category => {
            // The fee this category will pass down to its children.
            // It's its own explicit fee, or the one it inherited.
            const feeForChildren = category.fee_percentage !== null && category.fee_percentage !== undefined
                ? category.fee_percentage
                : inheritedFee;
            return (
              <React.Fragment key={category.id}>
                  <CategoryFeeRow
                      category={category}
                      level={level}
                      feeChanges={feeChanges}
                      onFeeChange={handleFeeChange}
                      inheritedFee={inheritedFee}
                    />
                  {category.children && renderCategoryRows(category.children, level + 1, feeForChildren)}
                </React.Fragment>
            );
        });
    };

    return (
        <div className="min-h-screen py-8">
            <div className="container mx-auto px-4">
                <h1 className="text-4xl font-bold text-white mb-8">Platform Fee Management</h1>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-gray-600">Set custom platform fee percentages for each category. If a fee is not set for a sub-category, it will inherit the fee from its parent. If no parent has a fee, the global default of <strong>{globalDefaultFee}%</strong> will be used.</p>
                        <p className="text-sm text-gray-500 mt-2">To reset a category to use the inherited/default fee, clear its input box and save.</p>
                    </div>

                    {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
                    {successMessage && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{successMessage}</div>}

                    {loading ? (
                        <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>
                    ) : (
                        <div className="border rounded-lg divide-y divide-gray-200">
                            {renderCategoryRows(categoryTree)}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <button 
                            onClick={handleSave}
                            disabled={isSaving || Object.keys(feeChanges).length === 0}
                            className="primary-gradient text-white font-bold py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function CategoryFeeRow({ category, level, feeChanges, onFeeChange, inheritedFee }) {
    // The value currently in the input box.
    const inputValue = feeChanges.hasOwnProperty(category.id) 
        ? feeChanges[category.id] 
        : category.fee_percentage;
    
    // If it's a virtual category (created by frontend), disable input
    const isVirtual = category.isVirtual;

    // The placeholder shows the fee that is currently in effect.
    let placeholderText;
    if (category.fee_percentage !== null && category.fee_percentage !== undefined) {
        // This category has an explicit fee set in the DB.
        placeholderText = `${category.fee_percentage}% (explicit)`;
    } else {
        // This category inherits its fee.
        placeholderText = `${inheritedFee}% (inherited)`;
    }

    return (
        <div 
            className="flex items-center justify-between p-3 hover:bg-gray-50"
            style={{ paddingLeft: `${level * 24 + 12}px` }}
        >
            <span className={`text-gray-800 font-medium ${isVirtual ? 'text-lg font-bold' : ''}`}>{category.name}</span>
            <div className="flex items-center gap-2">
                {!isVirtual && (
                <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={inputValue === null || inputValue === undefined ? '' : inputValue}
                    onChange={(e) => onFeeChange(category.id, e.target.value)}
                    placeholder={placeholderText}
                    className="w-32 text-right border-gray-300 rounded-md shadow-sm text-sm"
                    aria-label={`Fee for ${category.name}`}
                />
                )}
                {isVirtual && <span className="text-sm text-gray-400 italic">Group Header</span>}
                <span className="text-gray-500">%</span>
            </div>
        </div>
    );
}
